function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import * as React from 'react';
import orderBy from 'lodash/orderBy';
import groupBy from 'lodash/groupBy';
import { ControlBarReact } from 'common/widgets/control-bar';
import { ButtonReact } from 'common/components/button';
import { ButtonTheme, ButtonType } from 'common/components/button/constants';
import { Placement } from 'common/components/tooltip/types';
import { Section, SectionItem } from 'common/components/items-menu/types';
import { ContextPanelData } from 'common/widgets/context-panel/types';
import { defaultOrdering } from 'common/util/function';
import { useTranslation } from 'common/i18n/hooks';
import { Entry, isFilterEntry, isSegmentEntry } from '../types';
import { ControlBarAction, ControlBarActionItem, ControlBarActionItems, ControlBarActionValue } from '../constants';

/**
 * Groups overflow control items by their `section` property.
 */
function makeOverflowControlSections(items: ControlBarActionItem[]): Array<Section<ControlBarActionValue>> {
  const makeSection = (controlBarItems?: ControlBarActionItem[]) => {
    var _controlBarItems$;
    if (!controlBarItems) {
      return null;
    }
    return _objectSpread(_objectSpread({}, (_controlBarItems$ = controlBarItems[0]) !== null && _controlBarItems$ !== void 0 && _controlBarItems$.sectionLabel ? {
      label: controlBarItems[0].sectionLabel
    } : {}), {}, {
      items: orderBy(controlBarItems.filter(Boolean), "order", "asc")
    });
  };
  const sectionGroups = groupBy(items, "section");
  const sectionIds = Object.keys(sectionGroups).map(Number).filter(id => !Number.isNaN(id)).sort(defaultOrdering);
  const lastSectionId = Math.max(...sectionIds);
  const sections = [...sectionIds.map(id => id === lastSectionId ? null : makeSection(sectionGroups[id])),
  // all items without a specified section will display in the middle
  makeSection(sectionGroups["undefined"]), makeSection(sectionGroups[lastSectionId])].filter(Boolean);

  //Filter out empty sections
  return sections.filter(section => section.items.length > 0);
}
export function RowActions(props: {
  canDeleteEntry: boolean;
  canAlwaysDeleteEntry: boolean;
  disabled: boolean;
  entry: Entry;
  hasBehavioralFrequencyFilter: boolean;
  hasNewUI: boolean;
  isEmptyPlaceholder: boolean;
  onControlSelect: (value: ControlBarActionValue) => void;
  onClickDelete: () => void;
  overflowControls: ControlBarActionItem[];
  overflowControlsContextPanelTransform: (item: SectionItem) => ContextPanelData;
  renderActions?: () => React.ReactNode;
}) {
  var _controls$0$value, _props$renderActions;
  const i18n = useTranslation();
  const {
    t
  } = i18n;
  const overflowControls = makeOverflowControlSections(props.overflowControls);
  const controls = overflowControls.map(section => section.items).flat();
  const isDeleteOnly = controls.length === 1 && ((_controls$0$value = controls[0].value) === null || _controls$0$value === void 0 ? void 0 : _controls$0$value.action) === ControlBarAction.Delete;
  const showOverflowMenu = controls.length > 0 && !isDeleteOnly;
  const maxLabelLength = Math.max(...controls.map(control => typeof control.label === "string" ? control.label.length : control.label(i18n).length));
  const deleteButton = <ButtonReact key='entry-delete-action' elref='deleteLineButton' theme={ButtonTheme.Tertiary} icon={props.hasNewUI ? "x" : "trashcan"} role='button' disabled={props.disabled} type={ButtonType.Destructive} tooltip={{
    text: t("query-builder.actions.remove"),
    placement: Placement.Top
  }} onClick={() => props.onClickDelete()} />;
  return <div className='mp-query-entry-actions' data-sentry-component="RowActions" data-sentry-source-file="actions.tsx">
      {/** todo: move metric-only stuff out of base actions */}
      {!isFilterEntry(props.entry) && !isSegmentEntry(props.entry) && props.entry.isHidden && <ButtonReact key='entry-show-action' elref='showEntryButton' theme={ButtonTheme.Tertiary} icon='eye-hide' role='button' tooltip={{
      text: t("query-builder.actions.show-metric"),
      placement: Placement.Top
    }} onClick={() => props.onControlSelect(ControlBarActionItems[ControlBarAction.Show].value)} />}

      {props.renderActions && ((_props$renderActions = props.renderActions) === null || _props$renderActions === void 0 ? void 0 : _props$renderActions.call(props))}

      {/** row delete button */}
      {props.canDeleteEntry && !props.hasBehavioralFrequencyFilter && props.hasNewUI && (!props.isEmptyPlaceholder || props.canAlwaysDeleteEntry) && deleteButton}

      {!props.hasBehavioralFrequencyFilter && <div className='mp-query-control-bar-container'>
          {showOverflowMenu && <ControlBarReact key='entry-actions' elref='lineActions' theme={ButtonTheme.Tertiary} overflowIcon={props.hasNewUI ? "ellipsis" : "hor-ellipsis"} overflowControls={overflowControls} overflowMenuWidth={Math.max(maxLabelLength * 12, 200)} overflowMenuHeight={0} disabled={props.disabled} onActionSelect={_ref => {
        let {
          value
        } = _ref;
        return props.onControlSelect(value);
      }} contextPanelTransform={props.overflowControlsContextPanelTransform} />}
        </div>}

      {/** row delete button */}
      {props.canDeleteEntry && !props.hasBehavioralFrequencyFilter && !props.hasNewUI && !props.isEmptyPlaceholder && deleteButton}
    </div>;
}